/**
 * 国际化工具函数
 * 用于处理多语言内容和格式化
 */

/**
 * 从多语言对象中获取当前语言的值
 * @param {Object} multiLangObj - 多语言对象 {zh_CN: '中文', en_US: 'English'}
 * @param {string} currentLang - 当前语言 'zh-CN'
 * @param {string} fallback - 回退语言
 * @returns {string} 对应语言的值
 */
export const getLocalizedValue = (multiLangObj, currentLang, fallback = 'zh_CN') => {
  if (!multiLangObj) return '';
  
  // 将语言代码转换为API格式 (zh-CN -> zh_CN)
  const langKey = currentLang.replace('-', '_');
  
  // 尝试获取当前语言，失败则使用回退语言，再失败则使用第一个可用值
  return multiLangObj[langKey] || 
         multiLangObj[fallback] || 
         Object.values(multiLangObj)[0] || 
         '';
};

/**
 * 格式化日期（国际化）
 * @param {Date|string|number} date - 日期
 * @param {string} locale - 语言代码
 * @param {Object} options - Intl.DateTimeFormat 选项
 * @returns {string} 格式化后的日期
 */
export const formatDate = (date, locale = 'zh-CN', options = {}) => {
  try {
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  } catch (error) {
    console.error('Date formatting error:', error);
    return String(date);
  }
};

/**
 * 格式化数字（国际化）
 * @param {number} number - 数字
 * @param {string} locale - 语言代码
 * @param {Object} options - Intl.NumberFormat 选项
 * @returns {string} 格式化后的数字
 */
export const formatNumber = (number, locale = 'zh-CN', options = {}) => {
  try {
    return new Intl.NumberFormat(locale, options).format(number);
  } catch (error) {
    console.error('Number formatting error:', error);
    return String(number);
  }
};

/**
 * 格式化货币
 * @param {number} amount - 金额
 * @param {string} currency - 货币代码 (USD, CNY, SGD等)
 * @param {string} locale - 语言代码
 * @returns {string} 格式化后的货币
 */
export const formatCurrency = (amount, currency = 'SGD', locale = 'zh-CN') => {
  return formatNumber(amount, locale, {
    style: 'currency',
    currency
  });
};

/**
 * 获取相对时间描述（如：2天前）
 * @param {Date|string|number} date - 日期
 * @param {string} locale - 语言代码
 * @returns {string} 相对时间描述
 */
export const getRelativeTime = (date, locale = 'zh-CN') => {
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const now = new Date();
    const target = new Date(date);
    const diffInSeconds = Math.floor((target - now) / 1000);
    
    const units = [
      { unit: 'year', seconds: 31536000 },
      { unit: 'month', seconds: 2592000 },
      { unit: 'week', seconds: 604800 },
      { unit: 'day', seconds: 86400 },
      { unit: 'hour', seconds: 3600 },
      { unit: 'minute', seconds: 60 },
      { unit: 'second', seconds: 1 }
    ];
    
    for (const { unit, seconds } of units) {
      const value = Math.floor(diffInSeconds / seconds);
      if (Math.abs(value) >= 1) {
        return rtf.format(value, unit);
      }
    }
    
    return rtf.format(0, 'second');
  } catch (error) {
    console.error('Relative time formatting error:', error);
    return formatDate(date, locale);
  }
};
